<template>
  <div class="w-full max-w-2xl mx-auto">
    <label class="block text-md font-medium mb-2 text-white">{{ label }}</label>
    
    <div
      class="relative rounded-xl border-2 transition-all duration-300 overflow-hidden"
      :class="[
        isDragging 
          ? 'border-emerald-500 bg-emerald-500/10' 
          : selectedFile || initialPreviewUrl 
            ? 'border-solid border-emerald-400/50 bg-gradient-to-br from-emerald-900/30 to-indigo-900/30' 
            : 'border-dashed border-white/20 hover:border-emerald-400/50 bg-white/5 hover:bg-white/10'
      ]"
      @dragenter.prevent="isDragging = true"
      @dragleave.prevent="isDragging = false"
      @dragover.prevent
      @drop.prevent="handleDrop"
      @click="triggerFileInput"
    >
      <input
        type="file"
        ref="fileInput"
        class="hidden"
        @change="handleFileSelect"
        accept="audio/*" 
      />
      
      <!-- Estado vazio - sem arquivo selecionado -->
      <div v-if="!selectedFile && !initialPreviewUrl" class="flex flex-col items-center p-8 text-center">
        <div class="audio-icon-container mb-6">
          <div class="audio-icon">
            <div class="wave-bar" v-for="i in 5" :key="i" :style="{ animationDelay: `${i * 0.1}s` }"></div>
          </div>
        </div>
        <h3 class="text-md font-semibold text-white mb-2">Upload de Áudio</h3>
        <p class="text-white/70 mb-4 text-sm">
          Selecione um arquivo de áudio ou arraste e solte aqui
        </p>
        <div class="flex items-center justify-center gap-2 text-sm text-white/60">
          <span class="px-2 py-1 rounded-full bg-white/10">MP3</span>
          <span class="px-2 py-1 rounded-full bg-white/10">WAV</span>
          <span class="px-2 py-1 rounded-full bg-white/10">OGG</span>
          <span class="px-2 py-1 rounded-full bg-white/10">AAC</span>
        </div>
      </div>

      <!-- Estado com arquivo selecionado -->
      <div v-else class="p-6">
        <div class="flex flex-col">
          <!-- Informações do arquivo -->
          <div class="flex items-center mb-4">
            <div class="w-12 h-12 rounded-full bg-emerald-500/20 flex items-center justify-center mr-4">
              <MusicIcon class="w-6 h-6 text-emerald-400" />
            </div>
            <div class="flex-1 min-w-0">
              <h3 class="text-white font-medium truncate">
                {{ selectedFile ? selectedFile.name ?? title : (title || 'Áudio carregado') }}
              </h3>
              <p v-if="selectedFile" class="text-sm text-white/60">
                {{ formatFileSize(selectedFile.size) }}
              </p>
            </div>
            <button
              @click.stop="removeFile"
              class="ml-2 p-2 rounded-full hover:bg-white/10 transition-colors"
              aria-label="Remover áudio"
            >
              <XIcon class="w-5 h-5 text-white/70 hover:text-red-400" />
            </button>
          </div>
          
          <!-- Player de áudio com visualização -->
          <div class="relative bg-black/30 rounded-lg p-4 mb-4">
            <!-- Visualização de onda de áudio (simulada) -->
            <div class="audio-waveform mb-3">
              <div v-for="i in 40" :key="i" class="waveform-bar" 
                :style="{ 
                  height: `${Math.sin(i/3) * 20 + 30}%`,
                  opacity: i < currentPlayPosition ? 1 : 0.4
                }">
              </div>
            </div>
            
            <!-- Controles de áudio -->
            <div class="flex items-center">
              <button @click.stop="togglePlay" class="p-2 rounded-full bg-emerald-500 mr-3">
                <PlayIcon v-if="!isPlaying" class="w-4 h-4 text-white" />
                <PauseIcon v-else class="w-4 h-4 text-white" />
              </button>
              
              <div class="flex-1 relative">
                <div class="h-1 bg-white/20 rounded-full">
                  <div class="absolute top-0 left-0 h-1 bg-emerald-500 rounded-full" 
                    :style="{ width: `${audioProgress}%` }">
                  </div>
                </div>
              </div>
              
              <span class="ml-3 text-xs text-white/70">{{ formatTime(currentTime) }} / {{ formatTime(duration) }}</span>
            </div>
            
            <!-- Player de áudio real (escondido) -->
            <audio 
              ref="audioPlayer"
              :src="previewUrl || initialPreviewUrl"
              @timeupdate="updateProgress"
              @loadedmetadata="onAudioLoaded"
              @ended="isPlaying = false"
              class="hidden"
            ></audio>
          </div>
        </div>
      </div>

      <!-- Barra de progresso de upload -->
      <div
        v-if="uploading"
        class="absolute inset-x-0 bottom-0 bg-black/70 p-2"
      >
        <div class="relative h-1 bg-white/20 rounded-full overflow-hidden">
          <div 
            class="absolute top-0 left-0 h-full bg-gradient-to-r from-emerald-500 to-indigo-500 transition-all duration-300"
            :style="{ width: `${progress}%` }"
          ></div>
        </div>
        <div class="flex justify-between text-xs text-white/70 mt-1">
          <span>Enviando...</span>
          <span>{{ progress }}%</span>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, watch, onMounted, onBeforeUnmount } from 'vue'
import { XIcon, MusicIcon, PlayIcon, PauseIcon } from 'lucide-vue-next'

const props = defineProps({
  label: {
    type: String,
    default: 'Áudio'
  },
  modelValue: {
    type: Object,
    default: null
  },
  initialPreviewUrl: {
    type: String,
    default: ''
  },
  title: {
    type: String,
    default: ''
  }
})

const emit = defineEmits(['update:modelValue'])

const fileInput = ref(null)
const audioPlayer = ref(null)
const isDragging = ref(false)
const selectedFile = ref(props.modelValue)
const previewUrl = ref('')
const uploading = ref(false)
const progress = ref(0)
const isPlaying = ref(false)
const duration = ref(0)
const currentTime = ref(0)
const audioProgress = ref(0)
const currentPlayPosition = ref(0)

// Atualizar o previewUrl quando o modelValue mudar externamente
watch(() => props.modelValue, (newValue) => {
  if (newValue) {
    selectedFile.value = newValue
    previewUrl.value = URL.createObjectURL(newValue)
  } else {
    selectedFile.value = null
    previewUrl.value = ''
  }
})

// Inicializar com o valor do modelValue
onMounted(() => {
  if (props.modelValue) {
    selectedFile.value = props.modelValue
    previewUrl.value = URL.createObjectURL(props.modelValue)
  }
  
  // Atualizar a posição de reprodução para animação da forma de onda
  setInterval(() => {
    currentPlayPosition.value = Math.floor((audioProgress.value / 100) * 40)
  }, 100)
})

// Limpar URLs de objeto ao desmontar
onBeforeUnmount(() => {
  if (previewUrl.value) {
    URL.revokeObjectURL(previewUrl.value)
  }
})

const triggerFileInput = () => {
  if (!selectedFile.value) {
    fileInput.value?.click()
  }
}

const handleFileSelect = (event) => {
  const file = event.target.files?.[0]
  processFile(file)
}

const handleDrop = (event) => {
  isDragging.value = false
  const file = event.dataTransfer?.files[0]
  processFile(file)
}

const processFile = (file) => {
  if (!file) return
  
  if (!file.type.startsWith('audio/')) {
    alert('Por favor, selecione apenas arquivos de áudio.')
    return
  }

  if (file.size > 20 * 1024 * 1024) {
    alert('O arquivo deve ter menos que 20MB.')
    return
  }

  selectedFile.value = file
  previewUrl.value = URL.createObjectURL(file)
  emit('update:modelValue', file)
  simulateUpload()
}

const removeFile = (e) => {
  e.stopPropagation()
  selectedFile.value = null
  previewUrl.value = ''
  progress.value = 0
  uploading.value = false
  isPlaying.value = false
  duration.value = 0
  currentTime.value = 0
  audioProgress.value = 0
  
  if (fileInput.value) {
    fileInput.value.value = ''
  }
  
  emit('update:modelValue', null)
  emit('remove', null)
}

const simulateUpload = () => {
  uploading.value = true
  progress.value = 0
  
  const interval = setInterval(() => {
    progress.value += 5
    if (progress.value >= 100) {
      clearInterval(interval)
      uploading.value = false
    }
  }, 100)
}

const togglePlay = (e) => {
  e.stopPropagation()
  if (audioPlayer.value) {
    if (isPlaying.value) {
      audioPlayer.value.pause()
    } else {
      audioPlayer.value.play()
    }
    isPlaying.value = !isPlaying.value
  }
}

const updateProgress = () => {
  if (audioPlayer.value) {
    currentTime.value = audioPlayer.value.currentTime
    audioProgress.value = (currentTime.value / duration.value) * 100 || 0
  }
}

const onAudioLoaded = () => {
  if (audioPlayer.value) {
    duration.value = audioPlayer.value.duration
  }
}

const formatTime = (seconds) => {
  if (!seconds) return '0:00'
  const mins = Math.floor(seconds / 60)
  const secs = Math.floor(seconds % 60)
  return `${mins}:${secs.toString().padStart(2, '0')}`
}

const formatFileSize = (bytes) => {
  if (bytes < 1024) return bytes + ' bytes'
  else if (bytes < 1048576) return (bytes / 1024).toFixed(1) + ' KB'
  else return (bytes / 1048576).toFixed(1) + ' MB'
}
</script>

<style scoped>
.audio-icon-container {
  width: 80px;
  height: 80px;
  background: rgba(92, 246, 169, 0.15);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
}

.audio-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 3px;
  height: 40px;
}

.wave-bar {
  width: 4px;
  height: 20px;
  background-color: rgb(92, 246, 131);
  border-radius: 2px;
  animation: sound-wave 1.2s infinite ease-in-out;
}

@keyframes sound-wave {
  0%, 100% {
    height: 8px;
  }
  50% {
    height: 30px;
  }
}

.audio-waveform {
  display: flex;
  align-items: center;
  height: 60px;
  gap: 2px;
}

.waveform-bar {
  flex: 1;
  background-color: rgb(92, 246, 125);
  border-radius: 1px;
  transition: height 0.2s ease, opacity 0.3s ease;
}
</style>